import { useLocation } from 'react-router-dom';

import {
  PATH_MISSION_ACCOUNTING_INIT,
  PATH_COLLABORATION_MISSIONS_NEW,
  PATH_MISSION_ADDRESS_EDIT,
  PATH_MISSION_EXTERNAL_ACTORS_EDIT,
  PATH_MISSION_EXTERNAL_ACTORS_EDIT_CUSTOMIZE,
  PATH_MISSION_EXTERNAL_ACTORS_EDIT_DELETE,
  PATH_MISSION_EXTERNAL_ACTORS_EDIT_FLUSH,
  PATH_MISSION_EXTERNAL_ACTORS_EDIT_MODIFY,
  PATH_MISSION_EXTERNAL_ACTORS_EDIT_NEW,
  PATH_MISSION_GENERAL_EDIT,
  PATH_MISSION_GENERAL_EDIT_CODE,
  PATH_MISSION_GENERAL_EDIT_COLLABORATION,
  PATH_MISSION_GENERAL_EDIT_STATE,
  PATH_MISSION_INTERNAL_ACTORS_EDIT,
  PATH_MISSION_INTERNAL_ACTORS_EDIT_CUSTOMIZE,
  PATH_MISSION_INTERNAL_ACTORS_EDIT_DELETE,
  PATH_MISSION_INTERNAL_ACTORS_EDIT_FLUSH,
  PATH_MISSION_INTERNAL_ACTORS_EDIT_MODIFY,
  PATH_MISSION_INTERNAL_ACTORS_EDIT_NEW,
  PATH_MISSION_QUOTATION_INFO_EDIT,
  PATH_MISSION_QUOTATION_INFO_EDIT_STATE,
  PATH_MISSION_QUOTATION_INIT,
  PATH_MISSION_REPORTS_INIT,
  PATH_MISSION_SERVICE_USAGES_EDIT,
  PATH_MISSION_SERVICE_USAGES_EDIT_CUSTOMIZE_ALL,
  PATH_MISSION_SERVICE_USAGES_EDIT_CUSTOMIZE_ONE,
  PATH_MISSION_SERVICE_USAGES_EDIT_DELETE,
  PATH_MISSION_SERVICE_USAGES_EDIT_FLUSH_ALL,
  PATH_MISSION_SERVICE_USAGES_EDIT_FLUSH_ONE,
  PATH_MISSION_SERVICE_USAGES_EDIT_MODIFY,
  PATH_MISSION_SERVICE_USAGES_EDIT_NEW,
  PATH_MISSION_SERVICE_USAGES_EDIT_NEW_IN_GROUP,
  PATH_MISSION_SERVICE_USAGES_EDIT_REORDER,
  PATH_MISSION_SERVICE_USAGES_EDIT_SUB_MISSION,
  PATH_MISSION_TOOLKIT_EDIT,
} from '../../app/constants/paths.constant';
import { useIdParam } from '../../utils/use-id-param.util';

import { PageGate } from './PageGate';
import { REPORT_PAGES_SCOPES, ReportPagesGuard } from './ReportPagesGuard';
import { useMissionAccess } from './hooks/useMissionAccess.hook';
import { useOpenedAt } from './hooks/useOpenedAt.hook';
import { requiredScopeOf } from './utils/required-scope-of.util';
import type { MissionAccess } from './hooks/useMissionAccess.hook';

type MissionScope = Exclude<keyof MissionAccess, 'answeredAt'>;

// The scope each edition or creation page requires.
const MISSION_PAGES_SCOPES: readonly (readonly [string, MissionScope])[] = [
  [PATH_MISSION_GENERAL_EDIT, 'update'],
  [PATH_MISSION_GENERAL_EDIT_CODE, 'update'],
  [PATH_MISSION_GENERAL_EDIT_COLLABORATION, 'updateCollaborationLink'],
  [PATH_MISSION_GENERAL_EDIT_STATE, 'changeState'],
  [PATH_MISSION_ADDRESS_EDIT, 'updateAddress'],
  [PATH_MISSION_INTERNAL_ACTORS_EDIT, 'update'],
  [PATH_MISSION_INTERNAL_ACTORS_EDIT_DELETE, 'update'],
  [PATH_MISSION_INTERNAL_ACTORS_EDIT_FLUSH, 'update'],
  [PATH_MISSION_INTERNAL_ACTORS_EDIT_CUSTOMIZE, 'update'],
  [PATH_MISSION_INTERNAL_ACTORS_EDIT_MODIFY, 'update'],
  [PATH_MISSION_INTERNAL_ACTORS_EDIT_NEW, 'update'],
  [PATH_MISSION_EXTERNAL_ACTORS_EDIT, 'update'],
  [PATH_MISSION_EXTERNAL_ACTORS_EDIT_DELETE, 'update'],
  [PATH_MISSION_EXTERNAL_ACTORS_EDIT_FLUSH, 'update'],
  [PATH_MISSION_EXTERNAL_ACTORS_EDIT_CUSTOMIZE, 'update'],
  [PATH_MISSION_EXTERNAL_ACTORS_EDIT_MODIFY, 'update'],
  [PATH_MISSION_EXTERNAL_ACTORS_EDIT_NEW, 'update'],
  [PATH_MISSION_QUOTATION_INFO_EDIT, 'updateQuotation'],
  [PATH_MISSION_QUOTATION_INFO_EDIT_STATE, 'changeQuotationState'],
  [PATH_MISSION_QUOTATION_INIT, 'initQuotation'],
  [PATH_MISSION_SERVICE_USAGES_EDIT, 'updatePricing'],
  [PATH_MISSION_SERVICE_USAGES_EDIT_SUB_MISSION, 'updatePricing'],
  [PATH_MISSION_SERVICE_USAGES_EDIT_DELETE, 'updatePricing'],
  [PATH_MISSION_SERVICE_USAGES_EDIT_FLUSH_ALL, 'updatePricing'],
  [PATH_MISSION_SERVICE_USAGES_EDIT_FLUSH_ONE, 'updatePricing'],
  [PATH_MISSION_SERVICE_USAGES_EDIT_CUSTOMIZE_ALL, 'updatePricing'],
  [PATH_MISSION_SERVICE_USAGES_EDIT_CUSTOMIZE_ONE, 'updatePricing'],
  [PATH_MISSION_SERVICE_USAGES_EDIT_MODIFY, 'updatePricing'],
  [PATH_MISSION_SERVICE_USAGES_EDIT_NEW, 'updatePricing'],
  [PATH_MISSION_SERVICE_USAGES_EDIT_NEW_IN_GROUP, 'updatePricing'],
  [PATH_MISSION_SERVICE_USAGES_EDIT_REORDER, 'updatePricing'],
  [PATH_MISSION_TOOLKIT_EDIT, 'update'],
  [PATH_MISSION_REPORTS_INIT, 'initReport'],
  [PATH_MISSION_ACCOUNTING_INIT, 'initAccounting'],
  // A sub-mission created from its collaboration (the mission of the context), frozen by its
  // submitted quotation; the folder guard checks the right to create a mission.
  [PATH_COLLABORATION_MISSIONS_NEW, 'updateQuotationRelatedFields'],
];
// The edition and creation pages of a mission, reached directly by their URL, explain why they are
// refused to a user not allowed to use them. The page stays mounted while the answer is pending.
export const MissionPagesGuard = () => {
  const { pathname } = useLocation();
  const openedAt = useOpenedAt(pathname);
  const access = useMissionAccess(useIdParam('missionId'), pathname);
  const scope = requiredScopeOf(MISSION_PAGES_SCOPES, pathname);

  if (requiredScopeOf(REPORT_PAGES_SCOPES, pathname)) {
    return <ReportPagesGuard />;
  }

  return (
    <PageGate
      gate={scope ? access[scope] : undefined}
      isFresh={access.answeredAt >= openedAt}
    />
  );
};
